import { rasterSrc } from "@/generated/raster-images";
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, Section } from "@/components/site-blocks";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/consumer-duty-policy")({
  head: () => ({
    ...pageSeo({
      path: "/consumer-duty-policy",
      title: "Consumer Duty Policy | CRS Energy",
      description:
        "How CRS Energy meets its obligations under the FCA Consumer Duty as an Introducer Appointed Representative of HTG Finance Ltd.",
    }),
  }),
  component: ConsumerDutyPolicyPage,
});

function ConsumerDutyPolicyPage() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Consumer Duty Policy"
        description="How CRS Energy meets its obligations under the FCA Consumer Duty."
        image={rasterSrc("stock/photo-1573164574511-73c773193279")}
        imageAlt=""
      />
      <Section>
        <div className="max-w-3xl space-y-8 text-muted-foreground">
          <p className="text-lg font-semibold text-foreground">
            CRS Energy - Consumer Duty Policy
          </p>

          <section>
            <h2 className="text-xl font-semibold text-foreground">1. Purpose</h2>
            <p className="mt-2">
              This policy explains how CRS Energy meets its obligations under the FCA Consumer Duty.
              We operate as an Introducer Appointed Representative of HTG Finance Ltd ('Hometree'),
              who is the principal firm. Our main goal is to make sure all customers receive good
              results and are treated fairly.
            </p>
            <p className="mt-2">
              This policy follows the Financial Conduct Authority (FCA) rules for Consumer Duty
              (PRIN 12) and other relevant parts of the FCA Handbook.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">2. Scope</h2>
            <p className="mt-2">
              This policy applies to everyone at CRS Energy, including all staff and contractors.
              It covers anyone who helps introduce customers to Hometree's regulated products.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">3. Consumer Duty Principle</h2>
            <p className="mt-2">
              CRS Energy works to deliver good outcomes for customers. We support this through four
              key areas:
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <h3 className="text-base font-semibold text-foreground">3.1 Products and Services</h3>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>We only offer products and services that Hometree has checked and approved.</li>
                  <li>
                    We ensure our services are only offered to the right customers as identified by
                    Hometree.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-base font-semibold text-foreground">3.2 Price and Value</h3>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>Any information we share about prices or fees must be approved by Hometree first.</li>
                  <li>How we are paid will never be allowed to cause poor results for our customers.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-base font-semibold text-foreground">3.3 Consumer Understanding</h3>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>
                    We only introduce customers to Hometree; we do not provide financial advice
                    ourselves.
                  </li>
                  <li>All marketing materials are approved by Hometree before we use them.</li>
                  <li>Everything we say to customers will be clear, fair, and honest.</li>
                  <li>
                    We provide accurate information so customers understand our role as an
                    introducer.
                  </li>
                  <li>
                    If a customer has specific questions about Hometree's products, we will direct
                    them to Hometree for the answer.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-base font-semibold text-foreground">3.4 Consumer Support</h3>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>
                    We make it easy for customers to get the support they need without unnecessary
                    hurdles.
                  </li>
                  <li>Any complaints or detailed queries are passed to Hometree immediately.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">4. Governance and Oversight</h2>
            <ul className="mt-2 list-disc pl-6 space-y-1">
              <li>
                CRS Energy works closely with Hometree to provide any data or reports they need to
                check our standards.
              </li>
              <li>We keep clear records to prove we are following this policy.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">5. Training and Awareness</h2>
            <p className="mt-2">
              Every team member must complete the Consumer Duty training provided by Hometree.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">6. Review</h2>
            <p className="mt-2">
              We review this policy every year. We will also update it sooner if our activities
              change or if the FCA updates their rules.
            </p>
            <p className="mt-2">Last reviewed: August 2026</p>
          </section>
        </div>
      </Section>
    </>
  );
}
